import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTrash } from 'react-icons/fa';
import { RowItem, Group, RATE_OPTIONS } from '../../types';
import { formatCurrency } from '../../tipCalculationsNew';
import './TipTableRow.scss';

interface TipTableRowProps {
  row: RowItem;
  dates: string[];
  groups: Group[];
  expandedGroups: Set<string>;
  editingUserId: string | null;
  editingUserName: string;
  onToggleGroupExpanded: (groupId: string) => void;
  onStartAddUserToGroup: (groupId: string) => void;
  onConfirmUserName: (userId: string) => void;
  onCancelUserEdit: (userId: string) => void;
  onEditingUserNameChange: (name: string) => void;
  onUpdateUserRate: (userId: string, rate: number) => void;
  onUpdateGroupRate: (groupId: string, rate: number) => void;
  onUpdateAttendance: (itemId: string, date: string, checked: boolean) => void;
  calculateUserTip: (userId: string, date: string) => number;
  getUserTotalTips: (userId: string) => number;
  getGroupTipForDay: (groupId: string, date: string) => number;
  getGroupTotalTips: (groupId: string) => number;
  isViewMode?: boolean;
  onDeleteUser?: (userId: string, groupId?: string) => void;
  onDeleteGroup?: (groupId: string) => void;
}

// Rate Dropdown Component - Moved outside to prevent re-creation on parent render
const RateDropdown: React.FC<{
  currentRate: number;
  onRateChange: (rate: number) => void;
  rowId: string;
}> = ({ currentRate, onRateChange, rowId }) => {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRate = parseInt(e.target.value, 10);
    if (!isNaN(newRate)) {
      onRateChange(newRate);
    }
  };

  return (
    <div className="rate-dropdown-container">
      <select
        className="rate-dropdown-select"
        value={String(currentRate)}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {RATE_OPTIONS.map(rate => (
          <option key={rate} value={String(rate)}>
            {rate}%
          </option>
        ))}
      </select>
    </div>
  );
};

const TipTableRow: React.FC<TipTableRowProps> = ({
  row,
  dates,
  groups,
  expandedGroups,
  editingUserId,
  editingUserName,
  onToggleGroupExpanded,
  onStartAddUserToGroup,
  onConfirmUserName,
  onCancelUserEdit,
  onEditingUserNameChange,
  onUpdateUserRate,
  onUpdateGroupRate,
  onUpdateAttendance,
  calculateUserTip,
  getUserTotalTips,
  getGroupTipForDay,
  getGroupTotalTips,
  isViewMode = false,
  onDeleteUser,
  onDeleteGroup,
}) => {
  const isGroup = row.type === 'group';
  const group = groups.find(g => g.id === row.id);
  // Use the isRealGroup flag to determine if this is a real group container
  const isActualGroup = isGroup && row.isRealGroup === true;

  const getAttendance = (date: string): boolean => {
    if (isGroup) {
      return group?.attendance[date] ?? false;
    }
    const user = groups.flatMap(g => g.users).find(u => u.id === row.id);
    return user?.attendance[date] ?? groups.find(g => g.id === row.id)?.attendance[date] ?? false;
  };

  const handleRateChange = (newRate: number) => {
    if (isGroup) {
      // If it's a group entry (real group or standalone user), update as group
      onUpdateGroupRate(row.id, newRate);
    } else if (row.groupId) {
      // If it's a user within a group
      onUpdateUserRate(row.id, newRate);
    } else {
      // This case shouldn't happen, but fallback to updating as user
      onUpdateUserRate(row.id, newRate);
    }
  };

  return (
    <div className={`table-row ${isActualGroup ? 'group-row' : 'user-row'} level-${row.level}`}>
      {/* User/Group Name */}
      <div className="table-cell col-user">
        <div className="cell-content">
          {isActualGroup ? (
            <>
              <button
                className={`group-toggle ${expandedGroups.has(row.id) ? 'expanded' : ''}`}
                onClick={() => onToggleGroupExpanded(row.id)}
              >
                ▶
              </button>
              <span className="item-name group-name">{row.name}</span>
              {!isViewMode && (
                <>
                  <button
                    className="add-user-icon"
                    onClick={() => onStartAddUserToGroup(row.id)}
                    title="Añadir usuario al grupo"
                  >
                    +
                  </button>
                  <button
                    className="delete-icon"
                    onClick={() => onDeleteGroup?.(row.id)}
                    title="Eliminar grupo"
                  >
                    <FaTrash />
                  </button>
                </>
              )}
            </>
          ) : editingUserId === row.id ? (
            <div className="inline-edit-user">
              <input
                type="text"
                placeholder="Nombre del usuario"
                value={editingUserName}
                onChange={e => onEditingUserNameChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    onConfirmUserName(row.id);
                  } else if (e.key === 'Escape') {
                    onCancelUserEdit(row.id);
                  }
                }}
                autoFocus
                className="inline-input"
              />
              <button
                className="inline-btn-ok"
                onClick={() => onConfirmUserName(row.id)}
              >
                ✓
              </button>
              <button
                className="inline-btn-cancel"
                onClick={() => onCancelUserEdit(row.id)}
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <span className="item-name user-name">{row.name}</span>
              {!isViewMode && (
                <button
                  className="delete-icon"
                  onClick={() => onDeleteUser?.(row.id, row.groupId)}
                  title="Eliminar usuario"
                >
                  <FaTrash />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Rate - Custom Dropdown with Portal */}
      <div className="table-cell col-rate" data-label="Porcentaje">
        {isViewMode ? (
          <span className="rate-display">{row.rate}%</span>
        ) : (
          <RateDropdown 
            currentRate={row.rate} 
            onRateChange={handleRateChange}
            rowId={row.id}
          />
        )}
      </div>

      {/* Days */}
      {dates.map(date => (
        <div key={`${row.id}-${date}`} className="table-cell col-day" data-date={date}>
          <div className="cell-content">
            <input
              type="checkbox"
              checked={getAttendance(date)}
              onChange={e => onUpdateAttendance(row.id, date, e.target.checked)}
              className="attendance-checkbox"
              disabled={isViewMode}
            />
            {!isActualGroup && (
              <span className="tip-amount">
                {formatCurrency(calculateUserTip(row.id, date))}
              </span>
            )}
            {isActualGroup && (
              <span className="tip-amount group-tip">
                {formatCurrency(getGroupTipForDay(row.id, date))}
              </span>
            )}
          </div>
        </div>
      ))}

      {/* Total */}
      <div className="table-cell col-total">
        {!isActualGroup ? (
          <span className="total-amount">
            {formatCurrency(getUserTotalTips(row.id))}
          </span>
        ) : (
          <span className="total-amount group-total">
            {formatCurrency(getGroupTotalTips(row.id))}
          </span>
        )}
      </div>
    </div>
  );
};

export default TipTableRow;
